import 'braid-design-system/reset';
import {
  useExperimentValue,
  useFeatureFlag,
} from '@seek/candidate-feature-flag';

import {
  APPLICANT_INSIGHTS_NUMBER_OF_APPLICANTS_FEATURE_FLAG,
  type ApplicantInsightNumberOfApplicantsExperiment,
  BEHAVIOURAL_CUES_FILTERS_FEATURE_FLAG,
  BehaviouralCuesFilters,
  type BehaviouralCuesFiltersExperiment,
  DynamicPillsV2,
  JobCardRedesign,
  LOCATION_PROMPT_FEATURE_FLAG,
  type LocationPromptExperiment,
  LOGGED_OUT_HOMEPAGE_UPLIFT_FEATURE_FLAG,
  type LoggedOutHomepageUpliftExperiment,
  REFINE_BAR_V2_FEATURE_FLAG,
  RefineBarV2,
  type RefineBarV2Experiment,
  SAVED_AND_RECENT_SEARCHES_FEATURE_FLAG,
  type SavedAndRecentSearchesExperiment,
  SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
  type SearchQueryCorrectionExperiment,
  SERP_JOB_CARD_REDESIGN_FEATURE_FLAG,
  type SERPJobCardRedesignExperiment,
  StickySearchBar,
} from 'src/config/experiments';
import {
  HOME_FEEDBACK_LOOPS_HIDE_JOB_FEATURE_FLAG,
  type HomeFeedbackLoopsHideJobFeatureFlag,
  LD_HOMEPAGE_GENAI_CHAT_FEATURE_FLAG,
  type LDHomepageGenAIChatFeatureFlag,
  SOURCR_ON_SRP_JOB_CARD_FEATURE_FLAG,
  type SourcrOnSRPJobCardFeatureFlag,
} from 'src/config/featureFlags';
import { useCandidateDetails } from 'src/modules/hooks/useCandidateDetailsQuery.ts';

import { useSelector } from '../react';

import { selectFeatureFlag } from './selectors';

export const useIsHomeFeedAPIEnabled = () =>
  useSelector(selectFeatureFlag('homeFeedAPI'));

export const useIsCareerFeedCompanyEvpEnabled = () =>
  useSelector(selectFeatureFlag('careerFeedCompanyEvp'));

export const useIsHomepageGenAIChatKillSwitchEnabled = () =>
  !useLdHomepageGenAIChatFeatureFlag();

export const useIsHomepageGenAIChatEnabled = () => {
  const isHomepageGenAIChatEnabled = useSelector(
    selectFeatureFlag('homepageGenAIChat'),
  );
  const isKillSwitchEnabled = useIsHomepageGenAIChatKillSwitchEnabled();

  const allowedList: number[] = [582665568, 34304079];
  const { id: seekerId } = useCandidateDetails();

  return (
    !isKillSwitchEnabled &&
    (isHomepageGenAIChatEnabled ||
      Boolean(seekerId && allowedList.includes(seekerId)))
  );
};

export const useIsHomepageGenAIChatAltModelEnabled = () =>
  useSelector(selectFeatureFlag('homepageGenAIChatAltModel'));

export const useFeatureFlagRefineBarV2Experiment = () => {
  const value = useExperimentValue<RefineBarV2Experiment>(
    REFINE_BAR_V2_FEATURE_FLAG,
  );

  return {
    dynamicPillsV2: value === DynamicPillsV2,
    stickySearchBar: value === StickySearchBar,
    refineBarV2: value === RefineBarV2,
  };
};

export const useSavedAndRecentSearchesFeatureFlag = () => {
  const value = useExperimentValue<SavedAndRecentSearchesExperiment>(
    SAVED_AND_RECENT_SEARCHES_FEATURE_FLAG,
  );

  return value === 'saved-and-recent-searches';
};

export const useLdHomepageGenAIChatFeatureFlag = () => {
  const value = useFeatureFlag<LDHomepageGenAIChatFeatureFlag>(
    LD_HOMEPAGE_GENAI_CHAT_FEATURE_FLAG,
  );

  return value;
};

export const useLoggedOutHomepageUpliftFeatureFlag = () => {
  const value = useExperimentValue<LoggedOutHomepageUpliftExperiment>(
    LOGGED_OUT_HOMEPAGE_UPLIFT_FEATURE_FLAG,
  );

  return value;
};

export const useSERPJobCardRedesignFeatureFlag = () => {
  const value = useExperimentValue<SERPJobCardRedesignExperiment>(
    SERP_JOB_CARD_REDESIGN_FEATURE_FLAG,
  );

  return value === JobCardRedesign;
};

export const useBehaviouralCuesFiltersFeatureFlag = () => {
  const value = useExperimentValue<BehaviouralCuesFiltersExperiment>(
    BEHAVIOURAL_CUES_FILTERS_FEATURE_FLAG,
  );

  return value === BehaviouralCuesFilters;
};

export const useSearchQueryCorrectionFeatureFlag = () => {
  const value = useExperimentValue<SearchQueryCorrectionExperiment>(
    SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
  );
  return value === 'search-query-correction';
};

export const useSourcrOnSRPJobCardFeatureFlag = () =>
  useFeatureFlag<SourcrOnSRPJobCardFeatureFlag>(
    SOURCR_ON_SRP_JOB_CARD_FEATURE_FLAG,
  );

export const useHomeFeedbackLoopsHideJobFeatureFlag = () =>
  useFeatureFlag<HomeFeedbackLoopsHideJobFeatureFlag>(
    HOME_FEEDBACK_LOOPS_HIDE_JOB_FEATURE_FLAG,
  );

export const useApplicantInsightNumberOfApplicantsFeatureFlag = () =>
  useExperimentValue<ApplicantInsightNumberOfApplicantsExperiment>(
    APPLICANT_INSIGHTS_NUMBER_OF_APPLICANTS_FEATURE_FLAG,
  );

export const useEnableNumberOfApplicants = () => {
  const flag = useApplicantInsightNumberOfApplicantsFeatureFlag();
  return flag === 'volume' || flag === 'count';
};

export const useLocationPromptFeatureFlag = () => {
  const value = useExperimentValue<LocationPromptExperiment>(
    LOCATION_PROMPT_FEATURE_FLAG,
  );

  return value === 'location-prompt';
};

export const useIsHomepageGenAIChatDesktopDrawerEnabled = () =>
  useSelector(selectFeatureFlag('homepageGenAIChatDesktopDrawer'));
